import type { PressEvent } from 'react-aria-components';

import { Accordion } from '../../display/Accordion/Accordion';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Link } from '../../navigation/Link';
import styles from './FAQ.module.scss';

type FAQItem = {
  title: string;
  content: React.ReactNode;
  href?: string;
  isInitiallyOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

export type Props = {
  /** An array of FAQ items (accordions) to render. */
  items?: FAQItem[];
  /** Optional content to render in place of `items`. */
  children?: React.ReactNode;
  /** Optional override for section heading. */
  heading?: string;
  /** Text for optional call-to-action button. */
  ctaText?: string;
  /** URL for optional call-to-action button. */
  ctaHref?: string;
  /** Click handler for optional call-to-action button. */
  onCtaClick?: (e: PressEvent) => void;
};

/**
 * `FAQ` is used to group frequently asked questions ([Accordion](https://zinnia-storybook.zapier.com/?path=/docs/display-accordion--docs)'s) with a heading.
 */
export const FAQ = ({
  heading = 'Frequently Asked Questions',
  items,
  children,
  ctaText,
  ctaHref,
  onCtaClick,
}: Props) => {
  return (
    <div className={styles.root} data-zds>
      <div className={styles.faqContent} data-zds>
        <HeadingLevel>
          <Heading className={styles.heading}>{heading}</Heading>
        </HeadingLevel>
        {items && items.length > 0 ? (
          <div className={styles.grid} data-zds>
            {items.map((item) => (
              <Accordion
                key={item.title}
                title={item.title}
                href={item.href}
                isInitiallyOpen={item.isInitiallyOpen}
                isOpen={item.isOpen}
                onToggle={item.onToggle}
              >
                {item.content}
              </Accordion>
            ))}
          </div>
        ) : (
          children
        )}
        {ctaText && (ctaHref || onCtaClick) && (
          <div className={styles.ctaContainer} data-zds>
            <Link href={ctaHref} onPress={onCtaClick} variant="primary">
              {ctaText}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
